import React, { useState } from "react";
import { AuthService } from "../auth/authService";
import { Session } from "../auth/session";
import { STRONG_PASSWORD_MESSAGE, STRONG_PASSWORD_REGEX } from "../auth/passwordPolicy";

interface SetPasswordStepProps {
  // Called after saving or skipping; the user is already signed in either way
  onDone: () => void;
}

const inputClass =
  "w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-[#33ccbf] focus:ring-1 focus:ring-[#33ccbf] disabled:bg-gray-100 disabled:cursor-not-allowed";

// Optional after push login for accounts without a password, so they can also sign in
// with email + password later
const SetPasswordStep: React.FC<SetPasswordStepProps> = ({ onDone }) => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!STRONG_PASSWORD_REGEX.test(password)) return setError(STRONG_PASSWORD_MESSAGE);
    if (password !== confirmPassword) return setError("Passwords do not match.");
    setLoading(true);
    try {
      const token = await Session.getToken();
      if (!token) {
        onDone();
        return;
      }
      const res = await AuthService.setPassword(password, token);
      if (!res.success) {
        setError(res.error || "Failed to set the password.");
        return;
      }
      onDone();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-base font-bold text-gray-800 mb-1">Set a password</h2>
      <p className="text-xs text-gray-500 mb-5">
        Optional: add a password so you can also sign in with your email.
      </p>
      <form onSubmit={handleSave} className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <label htmlFor="set-password-input" className="text-xs font-semibold text-gray-700">
            Password
          </label>
          <input
            id="set-password-input"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            className={inputClass}
          />
          <p className="text-[10px] text-gray-500 mt-1 leading-normal">{STRONG_PASSWORD_MESSAGE}</p>
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="set-password-confirm-input" className="text-xs font-semibold text-gray-700">
            Confirm password
          </label>
          <input
            id="set-password-confirm-input"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading}
            className={inputClass}
          />
        </div>
        {error && (
          <div className="text-xs text-red-700 bg-red-50 border-l-4 border-red-600 px-3 py-2 rounded-lg leading-relaxed">
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={loading}
          className="w-full py-2.5 mt-2 text-sm font-semibold text-white bg-[#33ccbf] rounded-lg hover:bg-[#44E3D6] disabled:opacity-60 disabled:cursor-not-allowed transition-colors active:scale-[0.98]"
        >
          {loading ? "Saving..." : "Save password"}
        </button>
      </form>
      <div className="mt-6 text-center">
        <button
          type="button"
          onClick={onDone}
          disabled={loading}
          className="text-xs font-semibold text-gray-500 hover:text-gray-700 hover:underline focus:outline-none"
        >
          Skip for now
        </button>
      </div>
    </div>
  );
};

export default SetPasswordStep;
